import { useLanguage } from "@/contexts/LanguageContext";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  AlertCircle,
  Ticket,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BuyTicketPage = () => {
  const { t, language } = useLanguage();

  const formatDate = (date: string) => {
    if (language === "en") {
      return "May 10, 2025";
    }
    if (language === "ru") {
      return "10 мая 2025";
    }
    if (language === "uk") {
      return "10 травня 2025";
    }
    return date;
  };

  const formatTime = (time: string) => {
    if (language === "en") {
      const [start, end] = time.split(" - ");
      const formatHour = (hour: string) => {
        const [h, m] = hour.split(":");
        return `${h}:${m} ${Number(h) >= 12 ? "PM" : "AM"}`;
      };
      return `${formatHour(start)} - ${formatHour(end)}`;
    }
    return time;
  };

  const getTitle = () => {
    switch (language) {
      case "en":
        return "Buy Ticket";
      case "ru":
        return "Купить билет";
      case "uk":
        return "Купити квиток";
      default:
        return "Kup Bilet";
    }
  };

  const getSubtitle = () => {
    switch (language) {
      case "en":
        return "Grand Finale with Sam Childers in Poznań";
      case "ru":
        return "Гранд-финал с Сэмом Чайлдерсом в Познани";
      case "uk":
        return "Гранд-фінал з Семом Чайлдерсом у Познані";
      default:
        return "Wielki Finał z Samem Childersem w Poznaniu";
    }
  };

  const getEventPreviewAlt = () => {
    switch (language) {
      case "en":
        return "Event preview";
      case "ru":
        return "Предварительный просмотр мероприятия";
      case "uk":
        return "Попередній перегляд заходу";
      default:
        return "Zapowiedź wydarzenia";
    }
  };

  const getImportantInfo = () => {
    switch (language) {
      case "en":
        return "Important Information";
      case "ru":
        return "Важная информация";
      case "uk":
        return "Важлива інформація";
      default:
        return "Ważne Informacje";
    }
  };

  const getLimitedSeats = () => {
    switch (language) {
      case "en":
        return "Limited seats available!";
      case "ru":
        return "Ограниченное количество мест!";
      case "uk":
        return "Обмежена кількість місць!";
      default:
        return "Liczba miejsc ograniczona!";
    }
  };

  const getDateLabel = () => {
    switch (language) {
      case "en":
        return "Date:";
      case "ru":
        return "Дата:";
      case "uk":
        return "Дата:";
      default:
        return "Data:";
    }
  };

  const getTimeLabel = () => {
    switch (language) {
      case "en":
        return "Time:";
      case "ru":
        return "Время:";
      case "uk":
        return "Час:";
      default:
        return "Godzina:";
    }
  };

  const getLocationLabel = () => {
    switch (language) {
      case "en":
        return "Location:";
      case "ru":
        return "Место:";
      case "uk":
        return "Місце:";
      default:
        return "Miejsce:";
    }
  };

  const getEntryFeeLabel = () => {
    switch (language) {
      case "en":
        return "Entry fee:";
      case "ru":
        return "Стоимость входа:";
      case "uk":
        return "Вартість входу:";
      default:
        return "Opłata wstępu:";
    }
  };

  const getEntryFeeValue = () => {
    switch (language) {
      case "en":
        return "20 PLN - ticket required";
      case "ru":
        return "20 PLN - требуется билет";
      case "uk":
        return "20 PLN - потрібен квиток";
      default:
        return "20 zł - wymagany bilet";
    }
  };

  const getTicketInfo = () => {
    switch (language) {
      case "en":
        return "Tickets will be available at the venue if seats remain, but we encourage online purchase to avoid queues.";
      case "ru":
        return "Билеты будут доступны на месте, если останутся свободные места, но мы рекомендуем онлайн-покупку, чтобы избежать очередей.";
      case "uk":
        return "Квитки будуть доступні на місці, якщо залишаться вільні місця, але ми рекомендуємо онлайн-покупку, щоб уникнути черг.";
      default:
        return "Bilety będzie można nabyć również przed samym wydarzeniem – o ile pozostaną wolne miejsca – jednak zachęcamy do zakupu online, by uniknąć kolejek.";
    }
  };

  const getBuyTicketTitle = () => {
    switch (language) {
      case "en":
        return "Buy Event Ticket";
      case "ru":
        return "Купить билет на мероприятие";
      case "uk":
        return "Купити квиток на захід";
      default:
        return "Kup bilet na wydarzenie";
    }
  };

  const getEvenueInfo = () => {
    switch (language) {
      case "en":
        return "Event registration powered by evenea.pl";
      case "ru":
        return "Регистрация на мероприятие через evenea.pl";
      case "uk":
        return "Реєстрація на захід через evenea.pl";
      default:
        return "Rejestracja na wydarzenie obsługiwana przez evenea.pl";
    }
  };

  const getBusinessTitle = () => {
    switch (language) {
      case "en":
        return "Are you an entrepreneur?";
      case "ru":
        return "Вы предприниматель?";
      case "uk":
        return "Ви підприємець?";
      default:
        return "Jesteś przedsiębiorcą?";
    }
  };

  const getBusinessSubtitle = () => {
    switch (language) {
      case "en":
        return "Sam Childers for Business Leaders";
      case "ru":
        return "Сэм Чайлдерс для бизнес-лидеров";
      case "uk":
        return "Сем Чайлдерс для бізнес-лідерів";
      default:
        return "Sam Childers dla liderów biznesu";
    }
  };

  const getBusinessDescription = () => {
    switch (language) {
      case "en":
        return "May 9th (Friday), the day before the Grand Finale. An exclusive event for business leaders and entrepreneurs. Learn from Sam's unique experience in leading teams through extreme challenges and building successful organizations in the most demanding environments.";
      case "ru":
        return "9 мая (пятница), накануне Гранд-финала. Эксклюзивное мероприятие для бизнес-лидеров и предпринимателей. Узнайте об уникальном опыте Сэма в руководстве командами через экстремальные испытания и создании успешных организаций в самых сложных условиях.";
      case "uk":
        return "9 травня (п'ятниця), напередодні Гранд-фіналу. Ексклюзивна подія для бізнес-лідерів та підприємців. Дізнайтеся про унікальний досвід Сема в керівництві командами через екстремальні випробування та створенні успішних організацій у найскладніших умовах.";
      default:
        return "9 maja (piątek), dzień przed Wielkim Finałem. Ekskluzywne wydarzenie dla liderów biznesu i przedsiębiorców. Poznaj unikalne doświadczenia Sama w prowadzeniu zespołów przez ekstremalne wyzwania i budowaniu skutecznych organizacji w najbardziej wymagających środowiskach.";
    }
  };

  const getRegisterButton = () => {
    switch (language) {
      case "en":
        return "Register for Masterclass";
      case "ru":
        return "Зарегистрироваться на мастер-класс";
      case "uk":
        return "Зареєструватися на майстер-клас";
      default:
        return "Zarejestruj się na Masterclass";
    }
  };

  return (
    <div className="pt-16">
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto py-16 px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {getTitle()}
          </h1>
          <p className="text-xl opacity-90">
            {getSubtitle()}
          </p>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4 md:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="mb-8 overflow-hidden rounded-lg shadow-lg">
              <div className="aspect-[16/9] relative">
                <img
                  src="/preview.jpeg"
                  alt={getEventPreviewAlt()}
                  className="w-full h-full object-cover absolute inset-0"
                />
              </div>
            </div>
            <div className="bg-card border p-6 rounded-lg mb-8">
              <h2 className="flex items-center text-2xl font-bold mb-6">
                <AlertCircle className="mr-2 h-6 w-6 text-primary" />
                {getImportantInfo()}
              </h2>
              <p className="mb-4 text-red-600 font-semibold">
                {getLimitedSeats()}
              </p>
              <div className="flex flex-col space-y-4 mb-6">
                <div className="flex items-center">
                  <Calendar className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getDateLabel()}</strong>{" "}
                    {formatDate("10 maja 2025")}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getTimeLabel()}</strong>{" "}
                    {formatTime("17:00 - 19:00")}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getLocationLabel()}</strong>{" "}
                    {t("final.subtitle")}, {language === "en" ? "Hall" : language === "ru" ? "Зал" : language === "uk" ? "Зал" : "sala"}{" "}
                    B17
                  </span>
                </div>
                <div className="flex items-center">
                  <Users className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getEntryFeeLabel()}</strong>{" "}
                    {getEntryFeeValue()}
                  </span>
                </div>
                <div className="flex items-start mt-4 bg-muted/50 p-4 rounded-lg">
                  <Info className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    {getTicketInfo()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative md:mt-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg shadow-lg p-4 border">
                <h2 className="flex items-center text-2xl font-bold mb-6">
                  <Ticket className="mr-2 h-6 w-6 text-primary" />
                  {getBuyTicketTitle()}
                </h2>
                <p className="mb-6 text-muted-foreground">
                  {getEvenueInfo()}
                </p>
                <div className="relative" style={{ minHeight: "600px" }}>
                  <iframe
                    src="https://app.evenea.pl/event/kaznodziejazkarabinem-poznan/?out=1&source=event_iframe"
                    width="100%"
                    height="100%"
                    style={{
                      border: 0,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Leadership Event Section */}
      <div className="bg-black text-primary-foreground py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg md:text-xl text-primary mb-2">
                {getBusinessTitle()}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {getBusinessSubtitle()}
              </h2>
              <p className="text-xl mb-6 opacity-90">
                {getBusinessDescription()}
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <Calendar className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getDateLabel()}</strong>{" "}
                    {formatDate("9 maja 2025")}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getTimeLabel()}</strong>{" "}
                    {formatTime("17:00 - 19:00")}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{getLocationLabel()}</strong>{" "}
                    Hotel Mercure, Poznań
                  </span>
                </div>
              </div>
              <Button variant="secondary" size="lg" asChild>
                <a href="https://biznes.kaznodziejazkarabinem.pl" target="_blank" rel="noopener noreferrer">
                  {getRegisterButton()}
                </a>
              </Button>
            </div>
            <div className="hidden md:block">
              <img
                src="/images/sam-main (1).png"
                alt={language === "en" 
                  ? "Business leadership masterclass preview" 
                  : language === "ru"
                  ? "Предварительный просмотр мастер-класса по бизнес-лидерству"
                  : language === "uk"
                  ? "Попередній перегляд майстер-класу з бізнес-лідерства"
                  : "Zapowiedź masterclass przywództwa"}
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyTicketPage;
